// src/editor/AssetPicker.tsx
// Choosing a library file for one spec field: AssetSlot shows what's
// chosen, and opens AssetPicker, a searchable grid filtered to the kinds
// that field accepts. Image roles filter without restricting choice: the
// slot's preferred role is the starting filter, and "All" is one click away.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { IMAGE_ROLES, type AssetKind, type ImageRole } from '@ring/shared/assets';
import { ASSET_REF_PREFIX, BUILTIN_IMAGES, parseAssetRef, parseBuiltinRef } from '@ring/shared/projects';
import type { AssetSummary } from '../assetsApi';

const KIND_ICON: Record<AssetKind, string> = { image: '▢', music: '♪', video: '▶' };

function matches(a: AssetSummary, q: string): boolean {
  if (!q) return true;
  const hay = [a.filename, a.descShort, a.mood, a.bookCode, ...a.tags, ...a.roles].filter(Boolean).join(' ').toLowerCase();
  return q.toLowerCase().split(/\s+/).every(term => hay.includes(term));
}

export function AssetThumb({ asset, kind }: { asset: AssetSummary | undefined; kind?: AssetKind }) {
  return (
    <span className="editor-thumb">
      {asset?.thumbUrl
        ? <img src={asset.thumbUrl} alt="" loading="lazy" />
        : <span aria-hidden="true">{KIND_ICON[asset?.kind ?? kind ?? 'image']}</span>}
    </span>
  );
}

interface PickerProps {
  assets:  AssetSummary[];
  kinds:   readonly AssetKind[];
  /** Image roles to filter by at first. */
  roles?:  readonly ImageRole[];
  current: string | null;
  title:   string;
  onPick:  (assetId: string) => void;
  onClose: () => void;
}

export function AssetPicker({ assets, kinds, roles = [], current, title, onPick, onClose }: PickerProps) {
  const usable = useMemo(() => assets.filter(a => kinds.includes(a.kind)), [assets, kinds]);
  // Start on the preferred role only if something has it, so the grid never opens empty for no reason
  const [role, setRole] = useState<ImageRole | 'all'>(() => roles.find(r => usable.some(a => a.roles.includes(r))) ?? 'all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const shown = usable.filter(a => (role === 'all' || a.roles.includes(role)) && matches(a, query.trim()));
  const showRoles = kinds.includes('image');

  return (
    // No click-outside-to-close (like the Library panel): ✕ or Escape closes it
    <div className="editor-modal-backdrop">
      <div className="editor-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="editor-modal-head">
          <h3>{title}</h3>
          <button type="button" className="editor-icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="editor-picker-filters">
          <input type="search" placeholder="Search names, tags, descriptions" value={query} autoFocus
            onChange={e => setQuery(e.target.value)} />
          {showRoles && (
            <div className="editor-chips" role="group" aria-label="Role">
              {(['all', ...IMAGE_ROLES] as const).map(r => (
                <button key={r} type="button" className={`editor-chip${role === r ? ' editor-chip-on' : ''}`}
                  onClick={() => setRole(r)}>{r === 'all' ? 'All' : r}</button>
              ))}
            </div>
          )}
        </div>
        {usable.length === 0 ? (
          <p className="projects-sub">
            No {kinds.join(' or ')} in your <Link to="/dashboard/library">library</Link> yet.
          </p>
        ) : shown.length === 0 ? (
          <p className="projects-sub">Nothing matches. {role !== 'all' && <button type="button" className="editor-text-btn" onClick={() => setRole('all')}>Show all</button>}</p>
        ) : (
          <ul className="editor-picker-grid">
            {shown.map(a => (
              <li key={a.assetId}>
                <button type="button" className={`editor-picker-item${a.assetId === current ? ' editor-picker-current' : ''}`}
                  onClick={() => onPick(a.assetId)} title={a.descShort ?? a.filename}>
                  <AssetThumb asset={a} />
                  <span className="editor-picker-name">{a.filename}</span>
                  {a.roles.length > 0 && <span className="editor-picker-roles">{a.roles.join(', ')}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface SlotProps {
  label:    string;
  value:    unknown;
  assets:   AssetSummary[];
  assetsById: Map<string, AssetSummary>;
  kinds:    readonly AssetKind[];
  roles?:   readonly ImageRole[];
  /** `asset:<id>`, or "" for none. */
  onChange: (value: string) => void;
  /** Offer "None" (an empty file field: no music, no end card image). */
  allowNone?: boolean;
}

/**
 * One file field: the chosen asset (or none), with Choose/Change and Remove.
 * A built-in image (`builtin:<id>`, set from the JSON or a badge control) is
 * shown as itself; Change swaps it for a library file.
 */
export function AssetSlot({ label, value, assets, assetsById, kinds, roles, onChange, allowNone = true }: SlotProps) {
  const [open, setOpen] = useState(false);
  const id = parseAssetRef(value);
  const asset = id ? assetsById.get(id) : undefined;
  const builtinId = kinds.includes('image') ? parseBuiltinRef(value) : null;
  const builtin = builtinId ? BUILTIN_IMAGES[builtinId] : null;
  const chosen = Boolean(asset || builtin);
  const broken = (value !== '' && value !== undefined && value !== null) && !chosen;
  return (
    <div className="editor-slot">
      {builtin
        ? <span className="editor-thumb"><img src={`/builtin/${builtin.file}`} alt="" /></span>
        : <AssetThumb asset={asset} kind={kinds[0]} />}
      <span className="editor-slot-body">
        <span className="editor-slot-label">{label}</span>
        <span className={`editor-slot-name${broken ? ' editor-slot-broken' : ''}`}>
          {asset ? asset.filename : builtin ? builtin.label : broken ? 'Not in your library' : 'None'}
        </span>
      </span>
      <button type="button" className="projects-secondary editor-small" onClick={() => setOpen(true)}>{chosen ? 'Change' : 'Choose'}</button>
      {allowNone && (chosen || broken) && (
        <button type="button" className="editor-icon-btn" aria-label={`Remove ${label.toLowerCase()}`} onClick={() => onChange('')}>✕</button>
      )}
      {open && (
        <AssetPicker assets={assets} kinds={kinds} roles={roles} current={id} title={label}
          onClose={() => setOpen(false)}
          onPick={assetId => { onChange(ASSET_REF_PREFIX + assetId); setOpen(false); }} />
      )}
    </div>
  );
}
